<template>
    <div class="flex flex-col gap-1" :class="placement === 'sidebar' ? 'w-full' : 'w-screen md:w-full md:max-w-(--breakpoint-md) md:mx-auto shrink-0 mb-2 px-2 items-center'">
        <BaseButton
            theme="secondary"
            :class="{ 'w-full': placement === 'sidebar' }"
            data-testid="draft-self-review"
            :disabled="loading"
            class="flex items-center justify-center gap-2"
            @click="handleClick(false)">
            <LoadingSpinner v-if="loading" class="h-4" />
            <i v-else class="bi bi-workspace" aria-hidden="true"></i>
            {{ t("chat.selfReview.draftButton") }}
        </BaseButton>
        <div v-if="noticeMessage" class="body-small text-notification-error-text" role="alert">{{ noticeMessage }}</div>

        <ChatSelfReviewDialog
            v-model:open="dialogOpen"
            :sections="sections"
            :loaded-from-workday="loadedFromWorkday"
            :workday-unavailable="workdayUnavailable"
            :redrafting="redrafting"
            :redraft-error="redraftError"
            @redraft="handleClick(true)" />
    </div>
</template>

<script setup lang="ts">
import { ChatAction } from "~vue/chatActions";
import BaseButton from "~vue/components/BaseButton.vue";
import { useActionHandler } from "~vue/composables/useActionHandler";
import { useDeferredLoading } from "~vue/composables/useDeferredLoading";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { PERFORMANCE_REVIEW_EVENT_PREFIX } from "~vue/Moments/constants";
import type { SelfReviewSectionDraft } from "~vue/types/performance_management";
import { resolveActionRefusal } from "~vue/utils/actionRefusal";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { inject, ref } from "vue";
import { useI18n } from "vue-i18n";

import ChatSelfReviewDialog from "./ChatSelfReviewDialog.vue";

interface Props {
    latestMessageId: number;
    /** ``sidebar`` fills the performance chat's right-hand panel; ``chat`` sits above the composer. */
    placement?: "chat" | "sidebar";
}

const props = withDefaults(defineProps<Props>(), { placement: "chat" });

const { t } = useI18n();
const { execute } = useActionHandler();
const coachingSessionUuid = inject<string | undefined>("coachingSessionUuid", undefined);
const { loading, start: startLoading, stop: stopLoading } = useDeferredLoading();

const noticeMessage = ref("");
// A redraft is asked for from inside the open dialog, so its failure is shown there, not behind it.
const redraftError = ref("");
const redrafting = ref(false);
const dialogOpen = ref(false);
const sections = ref<SelfReviewSectionDraft[]>([]);
const loadedFromWorkday = ref(false);
const workdayUnavailable = ref(false);

function showError(redraft: boolean, message: string) {
    if (redraft) {
        redraftError.value = message;
    } else {
        noticeMessage.value = message;
    }
}

// ``redraft`` redrafts every section from the conversation, starting from what the colleague already
// wrote in Workday, instead of showing that as written.
async function handleClick(redraft: boolean) {
    startLoading();
    redrafting.value = redraft;
    noticeMessage.value = "";
    redraftError.value = "";
    const event = redraft ? "redraft_self_review_clicked" : "draft_self_review_clicked";
    logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}${event}`, { session_id: coachingSessionUuid }, coachingSessionUuid);

    try {
        const result = await resolveActionRefusal(execute(ChatAction.DRAFT_SELF_REVIEW_CARD, { message_id: props.latestMessageId, redraft }, { sync: true }));
        if (result?.success && result.payload?.sections?.length) {
            sections.value = result.payload.sections;
            loadedFromWorkday.value = Boolean(result.payload.loaded_from_workday);
            workdayUnavailable.value = Boolean(result.payload.workday_unavailable);
            dialogOpen.value = true;
        } else {
            showError(redraft, result?.payload?.error ?? t("chat.selfReview.draftError"));
        }
    } catch (e) {
        logError(e);
        showError(redraft, t("chat.selfReview.draftError"));
    } finally {
        stopLoading();
        redrafting.value = false;
    }
}
</script>
